import { AnimatePresence, motion } from "framer-motion";
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useMediaQuery from "../../hooks/useMediaQuery";
import brokenImage from "../../images/brokenImage.jpg";

const ImageCarousel = ({ images, imageURL, loading }) => {
    const navigate = useNavigate();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isImgLoading, setIsImgLoading] = useState(true);
    const isAbove600px = useMediaQuery("(min-width:600px)");
    const [startX, setStartX] = useState(null);
    const imageRef = useRef(null);

    if (!images) {
        return <div>No images available</div>;
    }

    const scrollCarousel = (direction) => {
        setIsImgLoading(true);
        if (direction === "left") {
            setCurrentIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : images.length - 1));
        } else if (direction === "right") {
            setCurrentIndex((prevIndex) => (prevIndex < images.length - 1 ? prevIndex + 1 : 0));
        }
    };

    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
        document.body.style.overflow = isFullScreen ? "auto" : "hidden";
    };

    const handleSwipe = (startX, endX) => {
        const diffX = startX - endX;
        if (Math.abs(diffX) > 50) {
            if (diffX > 0) {
                scrollCarousel("right");
            } else {
                scrollCarousel("left");
            }
        }
    };

    const onTouchStart = (e) => {
        if (images.length < 2) return;
        setStartX(e.touches[0].clientX);
    };

    const onTouchEnd = (e) => {
        if (images.length < 2) return;
        if (startX !== null) {
            const endX = e.changedTouches[0].clientX;
            handleSwipe(startX, endX);
            setStartX(null);
        }
    };

    const styles = {
        carouselContainer: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            width: "100%",
            maxWidth: "700px",
            margin: "auto",
        },
        arrowButtonLeft: {
            position: "absolute",
            left: "10px",
            zIndex: 1,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            cursor: "pointer",
            fontSize: "20px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
        },
        arrowButtonRight: {
            position: "absolute",
            right: "10px",
            zIndex: 1,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            // backdropFilter: "blur(10px)",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            cursor: "pointer",
            fontSize: "20px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
        },
        imageWrapper: {
            width: "100%",
            height: "420px",
            position: "relative",
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        },
        imageContainer: (isFullScreen) => ({
            position: isFullScreen ? "fixed" : "relative",
            top: isFullScreen ? 0 : "auto",
            left: isFullScreen ? 0 : "auto",
            width: isFullScreen ? "100vw" : "100%",
            height: isFullScreen ? "100vh" : "420px",
            backgroundColor: isFullScreen ? "black" : "transparent",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: isFullScreen ? 9999 : "auto",
            cursor: isFullScreen ? "pointer" : "default",
        }),
        image: (isFullScreen) => ({
            width: "100%",
            height: "100%",
            objectFit: isFullScreen ? "contain" : "cover",
            borderRadius: isFullScreen ? 0 : "8px",
            filter: isImgLoading ? "blur(10px)" : "none",
            transition: "filter 0.3s ease-out",
        }),
        fullScreenControls: {
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 10000,
        },
        fullScreenCloseButton: {
            backgroundColor: "lightgrey",
            backdropFilter: "blur(10px)",
            border: "none",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            color: "black",
            fontSize: "25px",
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
        },
        fullScreenNav: {
            position: "fixed",
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            padding: "0 20px",
            boxSizing: "border-box",
            zIndex: 10000,
            top: "50%",
            transform: "translateY(-50%)",
        },
        fullScreenArrow: {
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            border: "none",
            borderRadius: "50%",
            width: "50px",
            height: "50px",
            color: "white",
            fontSize: "24px",
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
        },
        dotsContainer: (isFullScreen) => ({
            position: isFullScreen ? "fixed" : "absolute",
            bottom: isFullScreen ? "30px" : "15px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            zIndex: isFullScreen ? 10000 : "auto",
        }),
        dot: (isFullScreen, active) => ({
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            margin: "0 6px",
            cursor: "pointer",
            transition: "background-color 0.3s ease",
            backgroundColor: active ? (isFullScreen ? "white" : "#007bff") : isFullScreen ? "rgba(255, 255, 255, 0.5)" : "#ccc",
        }),
        skeletonLoader: {
            width: "100%",
            height: "100%",
            backgroundColor: "#e0e0e0",
            borderRadius: "8px",
            position: "absolute",
            top: 0,
            left: 0,
        },
        skeletonPulse: {
            animation: "pulse 1.5s infinite ease-in-out",
        },
    };

    const animationStyles = `
        @keyframes pulse {
            0%, 100% {
                opacity: 0.6;
            }
            50% {
                opacity: 1;
            }
        }
    `;

    return (
        <>
            <style>{animationStyles}</style>

            <div style={styles.carouselContainer}>
                {!isAbove600px && !isFullScreen && (
                    <div
                        onClick={() => navigate(-1)}
                        style={{
                            position: "absolute",
                            top: "10px",
                            left: "10px",
                            zIndex: 1,
                            cursor: "pointer",
                            padding: "0",
                        }}
                    >
                        <button
                            style={{
                                // backgroundColor: "rgba(0, 0, 0, 0.43)",
                                backdropFilter: "blur(10px)",
                                backgroundColor: "rgba(0, 0, 0, 0.7)",
                                border: "none",
                                borderRadius: "50%",
                                width: "40px",
                                height: "40px",
                                color: "white",
                                fontSize: "24px",
                                cursor: "pointer",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                WebkitTapHighlightColor: "transparent",
                            }}
                        >
                            {"<"}
                        </button>
                    </div>
                )}

                {isAbove600px && !isFullScreen && (
                    <button style={styles.arrowButtonLeft} onClick={() => scrollCarousel("left")}>
                        {"<"}
                    </button>
                )}

                <div style={styles.imageWrapper} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                    {(loading || isImgLoading) && <div style={{ ...styles.skeletonLoader, ...styles.skeletonPulse }}></div>}

                    <AnimatePresence>
                        <motion.div
                            key={currentIndex + (isFullScreen ? "full" : "normal")}
                            style={styles.imageContainer(isFullScreen)}
                            onClick={() => {
                                if (!isFullScreen) toggleFullScreen();
                            }}
                            initial={{ opacity: isFullScreen ? 1.5 : 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <img
                                ref={imageRef}
                                src={imageURL + images[currentIndex]?.PROMO_IMAGE}
                                alt={`Carousel ${currentIndex + 1}`}
                                style={styles.image(isFullScreen)}
                                onError={(e) => {
                                    e.target.src = brokenImage;
                                    setIsImgLoading(false);
                                }}
                                onLoad={() => {
                                    setIsImgLoading(false);
                                }}
                                loading="lazy"
                            />
                        </motion.div>
                    </AnimatePresence>

                    {images.length > 1 && !loading && (
                        <div style={styles.dotsContainer(isFullScreen)}>
                            {images.map((_, index) => (
                                <div key={index} onClick={() => setCurrentIndex(index)} style={styles.dot(isFullScreen, currentIndex === index)} />
                            ))}
                        </div>
                    )}
                </div>

                {isAbove600px && !isFullScreen && (
                    <button style={styles.arrowButtonRight} onClick={() => scrollCarousel("right")}>
                        {">"}
                    </button>
                )}

                {isFullScreen && (
                    <>
                        <div style={styles.fullScreenControls}>
                            <button style={styles.fullScreenCloseButton} onClick={toggleFullScreen} aria-label="Close fullscreen">
                                ×
                            </button>
                        </div>

                        {isAbove600px && (
                            <div style={styles.fullScreenNav}>
                                <button style={styles.fullScreenArrow} onClick={() => scrollCarousel("left")} aria-label="Previous image">
                                    {"<"}
                                </button>
                                <button style={styles.fullScreenArrow} onClick={() => scrollCarousel("right")} aria-label="Next image">
                                    {">"}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default ImageCarousel;
